import * as React from "react"
import {
  DimensionSortMode,
  DirectionFromOrigin,
  type IntervalOrigin,
  MissingObject,
  type SummaryFilterInput,
  useSummarizeTransactionsByPartyQuery,
  useSummarizeTransactionsWithoutDimensionQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import objectHelper from "@digits-shared/helpers/objectHelper"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const NO_PARTY_LABELS = ["No Party", "None", "Empty", "Missing Party"]

export function usePartyFilters({
  filters,
  searchTerm,
  includeMissingParty,
  origin,
  skip = false,
}: {
  filters: Omit<SummaryFilterInput, "viewKey">
  searchTerm: string
  includeMissingParty: true | undefined
  origin?: IntervalOrigin
  skip?: boolean
}) {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()

  const { data, loading: loadingParties } = useSummarizeTransactionsByPartyQuery({
    variables: {
      limit: 100,
      sort: DimensionSortMode.Active,
      filter: {
        ...filters,
        viewKey,
      },
      direction: DirectionFromOrigin.Past,
      origin: {
        ...(origin || intervalOrigin),
        intervalCount: origin?.intervalCount || intervalOrigin.intervalCount || 1,
      },
    },
    skip,
  })

  const { data: noPartyData, loading: loadingNoPartyCheck } =
    useSummarizeTransactionsWithoutDimensionQuery({
      variables: {
        filter: {
          ...filters,
          missingParty: MissingObject.Include,
          viewKey,
        },
        direction: DirectionFromOrigin.Past,
        origin: {
          ...(origin || intervalOrigin),
          intervalCount: origin?.intervalCount || intervalOrigin.intervalCount || 1,
        },
      },
    })

  const parties = React.useMemo(
    () =>
      data?.dimensionalTransactionSummary.party
        .map((summary) => summary.partyObject)
        .filter((party) => party.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort(objectHelper.compareObjectNames) || [],
    [data?.dimensionalTransactionSummary.party, searchTerm]
  )

  const showNoPartyOption = React.useMemo(() => {
    const transactionCount =
      noPartyData?.dimensionalTransactionSummary?.summary?.total?.transactionsCount || 0

    return (
      (transactionCount > 0 || includeMissingParty) &&
      NO_PARTY_LABELS.some((label) => label.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [includeMissingParty, noPartyData, searchTerm])

  return { parties, showNoPartyOption, loading: loadingParties || loadingNoPartyCheck }
}
